import { adminSetPortalRole, fetchPortalMembersByKind } from './accessManagement';
import { supabaseCrm } from './supabase';
import { formatSupabaseError } from './formatSupabaseError';
import {
  addCalendarDays,
  daysBetween,
  formatYmd,
  listRecurrenceTodoDates,
  normalizeRecurrenceRule,
  parseYmd,
} from './calendarRecurrence';
import type {
  CalendarRecurrenceRule,
  CalendarTask,
  CalendarTaskAttachment,
  CalendarTaskInput,
  CalendarTaskStatus,
  CalendarTrip,
  CalendarTripInput,
} from '../types/calendarWorkspace';

export const DEFAULT_CALENDAR_SLUG = 'jason_chong';
export const CALENDAR_ATTACHMENTS_BUCKET = 'calendar-attachments';

const TASK_SELECT =
  'id,workspace_slug,title,description,status,progress,request_date,todo_date,deadline,assignee_email,created_by,trip_id,thread_id,thread_order,recurrence_series_id,recurrence_rule,recurrence_index,created_at,updated_at';

const TRIP_SELECT =
  'id,workspace_slug,title,location,notes,starts_on,ends_on,owner_email,created_by,created_at,updated_at';

const ATTACH_SELECT =
  'id,task_id,thread_id,file_name,content_type,byte_size,storage_path,uploaded_by,created_at';

function db() {
  if (!supabaseCrm) throw new Error('Supabase is not configured.');
  return supabaseCrm;
}

function normalizeEmail(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

export function calendarTodayYmd(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function isCalendarTaskOverdue(
  task: Pick<CalendarTask, 'deadline' | 'status'>,
  today = calendarTodayYmd(),
): boolean {
  return task.status !== 'done' && Boolean(task.deadline) && task.deadline < today;
}

function horizonYmd(monthsAhead = 12): string {
  const d = new Date();
  d.setMonth(d.getMonth() + monthsAhead);
  return formatYmd(d);
}

function buildOccurrenceRow(input: {
  id: string;
  workspaceSlug: string;
  title: string;
  description: string;
  status: CalendarTaskStatus;
  progress: number;
  todoDate: string;
  requestOffsetDays: number;
  deadlineOffsetDays: number;
  assigneeEmail: string;
  createdBy: string;
  tripId: string | null;
  seriesId: string | null;
  rule: CalendarRecurrenceRule | null;
  index: number;
}): Record<string, unknown> {
  const todo = parseYmd(input.todoDate);
  const request = addCalendarDays(todo, input.requestOffsetDays);
  const deadline = addCalendarDays(todo, input.deadlineOffsetDays);
  return {
    id: input.id,
    workspace_slug: input.workspaceSlug,
    title: input.title,
    description: input.description,
    status: input.status,
    progress: input.progress,
    request_date: formatYmd(request),
    todo_date: input.todoDate,
    deadline: formatYmd(deadline),
    assignee_email: input.assigneeEmail,
    created_by: input.createdBy,
    trip_id: input.tripId,
    thread_id: input.id,
    thread_order: 1,
    recurrence_series_id: input.seriesId,
    recurrence_rule: input.rule,
    recurrence_index: input.index,
    updated_at: new Date().toISOString(),
  };
}

export async function isCalendarAllowlisted(email: string | null | undefined): Promise<boolean> {
  const e = normalizeEmail(email);
  if (!e) return false;
  const members = await fetchPortalMembersByKind('calendar');
  return members.some((row) => normalizeEmail(row.email) === e);
}

export async function listCalendarTasks(workspaceSlug: string): Promise<CalendarTask[]> {
  await ensureRecurringHorizon(workspaceSlug).catch(() => undefined);
  const { data, error } = await db()
    .from('calendar_tasks')
    .select(TASK_SELECT)
    .eq('workspace_slug', workspaceSlug)
    .order('todo_date', { ascending: true })
    .order('thread_order', { ascending: true });
  if (error) throw new Error(formatSupabaseError(error));
  return (data ?? []) as CalendarTask[];
}

export async function listCalendarTasksInRange(
  workspaceSlug: string,
  from: string,
  to: string,
): Promise<CalendarTask[]> {
  const { data, error } = await db()
    .from('calendar_tasks')
    .select(TASK_SELECT)
    .eq('workspace_slug', workspaceSlug)
    .gte('todo_date', from)
    .lte('todo_date', to)
    .order('todo_date', { ascending: true });
  if (error) throw new Error(formatSupabaseError(error));
  return (data ?? []) as CalendarTask[];
}

/** Normalize DB / ISO dates to YYYY-MM-DD for reliable chronological compare. */
function calendarDayKey(value: string | null | undefined): string {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(raw);
  return m?.[1] ?? raw.slice(0, 10);
}

/** Display order: chronological by to-do date (never by creation / thread_order alone). */
export function sortThreadTasksByDate(tasks: CalendarTask[]): CalendarTask[] {
  return [...tasks].sort((a, b) => {
    const aDay = calendarDayKey(a.todo_date);
    const bDay = calendarDayKey(b.todo_date);
    // Empty dates last
    if (aDay !== bDay) {
      if (!aDay) return 1;
      if (!bDay) return -1;
      return aDay.localeCompare(bDay);
    }
    const byRequest = calendarDayKey(a.request_date).localeCompare(calendarDayKey(b.request_date));
    if (byRequest !== 0) return byRequest;
    if (a.thread_order !== b.thread_order) return a.thread_order - b.thread_order;
    return a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });
  });
}

/** Root / first node of a thread (owns shared request_date + deadline). */
export function threadRootTask(tasks: CalendarTask[]): CalendarTask | undefined {
  if (tasks.length === 0) return undefined;
  return [...tasks].sort((a, b) => {
    if (a.thread_order !== b.thread_order) return a.thread_order - b.thread_order;
    return a.todo_date.localeCompare(b.todo_date);
  })[0];
}

export async function listThreadTasks(threadId: string): Promise<CalendarTask[]> {
  const { data, error } = await db()
    .from('calendar_tasks')
    .select(TASK_SELECT)
    .eq('thread_id', threadId)
    .order('todo_date', { ascending: true })
    .order('thread_order', { ascending: true });
  if (error) throw new Error(formatSupabaseError(error));
  return sortThreadTasksByDate((data ?? []) as CalendarTask[]);
}

export async function createCalendarTask(
  workspaceSlug: string,
  input: CalendarTaskInput,
  actorEmail: string,
): Promise<CalendarTask> {
  const createdBy = normalizeEmail(actorEmail);
  const id = crypto.randomUUID();
  const threadId = input.thread_id?.trim() || id;
  let threadOrder = input.thread_order ?? 1;
  let requestDate = input.request_date;
  let deadline = input.deadline;
  const isFollowUp = Boolean(input.thread_id?.trim());
  const rule = !isFollowUp ? normalizeRecurrenceRule(input.recurrence_rule) : null;

  if (isFollowUp) {
    const siblings = await listThreadTasks(threadId);
    if (input.thread_order == null) {
      threadOrder = siblings.reduce((max, t) => Math.max(max, t.thread_order), 0) + 1;
    }
    const first = threadRootTask(siblings);
    if (first) {
      requestDate = first.request_date;
      deadline = first.deadline;
    }
  }

  if (rule) {
    const seriesId = crypto.randomUUID();
    const requestOffset = daysBetween(input.todo_date, requestDate);
    const deadlineOffset = daysBetween(input.todo_date, deadline);
    const dates = listRecurrenceTodoDates(input.todo_date, rule, {
      maxCount: 52,
      horizonYmd: horizonYmd(12),
    });
    const title = input.title.trim();
    const description = (input.description ?? '').trim();
    const assignee = normalizeEmail(input.assignee_email) || createdBy;
    const rows = dates.map((todoDate, index) => {
      const occId = index === 0 ? id : crypto.randomUUID();
      return buildOccurrenceRow({
        id: occId,
        workspaceSlug,
        title,
        description,
        status: index === 0 ? ((input.status ?? 'todo') as CalendarTaskStatus) : 'todo',
        progress: index === 0 ? (input.progress ?? 0) : 0,
        todoDate,
        requestOffsetDays: requestOffset,
        deadlineOffsetDays: deadlineOffset,
        assigneeEmail: assignee,
        createdBy,
        tripId: input.trip_id ?? null,
        seriesId,
        rule,
        index,
      });
    });
    const { data, error } = await db().from('calendar_tasks').insert(rows).select(TASK_SELECT);
    if (error) throw new Error(formatSupabaseError(error));
    const first = (data as CalendarTask[] | null)?.find((t) => t.id === id) ?? (data as CalendarTask[])?.[0];
    if (!first) throw new Error('Could not create recurring tasks.');
    return first;
  }

  const row = {
    id,
    workspace_slug: workspaceSlug,
    title: input.title.trim(),
    description: (input.description ?? '').trim(),
    status: (input.status ?? 'todo') as CalendarTaskStatus,
    progress: input.progress ?? 0,
    request_date: requestDate,
    todo_date: input.todo_date,
    deadline,
    assignee_email: normalizeEmail(input.assignee_email) || createdBy,
    created_by: createdBy,
    trip_id: input.trip_id ?? null,
    thread_id: threadId,
    thread_order: threadOrder,
    recurrence_series_id: null,
    recurrence_rule: null,
    recurrence_index: 0,
    updated_at: new Date().toISOString(),
  };

  const { data, error } = await db().from('calendar_tasks').insert(row).select(TASK_SELECT).single();
  if (error) throw new Error(formatSupabaseError(error));
  return data as CalendarTask;
}

/** Extend open-ended recurring series so upcoming occurrences exist through the horizon. */
export async function ensureRecurringHorizon(workspaceSlug: string): Promise<void> {
  const { data, error } = await db()
    .from('calendar_tasks')
    .select(TASK_SELECT)
    .eq('workspace_slug', workspaceSlug)
    .not('recurrence_series_id', 'is', null);
  if (error) throw new Error(formatSupabaseError(error));
  const tasks = (data ?? []) as CalendarTask[];
  if (tasks.length === 0) return;

  const bySeries = new Map<string, CalendarTask[]>();
  for (const t of tasks) {
    if (!t.recurrence_series_id) continue;
    const list = bySeries.get(t.recurrence_series_id) ?? [];
    list.push(t);
    bySeries.set(t.recurrence_series_id, list);
  }

  const horizon = horizonYmd(12);
  const inserts: Record<string, unknown>[] = [];

  for (const [seriesId, list] of bySeries) {
    const sorted = [...list].sort((a, b) => a.recurrence_index - b.recurrence_index);
    const root = sorted[0];
    if (!root) continue;
    const rule = normalizeRecurrenceRule(root.recurrence_rule);
    if (!rule) continue;

    const existingTodos = new Set(sorted.map((t) => t.todo_date));
    const maxIndex = sorted.reduce((m, t) => Math.max(m, t.recurrence_index), 0);
    const last = sorted[sorted.length - 1];
    if (last && last.todo_date >= horizon) continue;

    const requestOffset = daysBetween(root.todo_date, root.request_date);
    const deadlineOffset = daysBetween(root.todo_date, root.deadline);
    const dates = listRecurrenceTodoDates(root.todo_date, rule, {
      maxCount: 80,
      horizonYmd: horizon,
    });

    let nextIndex = maxIndex + 1;
    for (const todoDate of dates) {
      if (existingTodos.has(todoDate)) continue;
      if (todoDate <= (last?.todo_date ?? root.todo_date)) continue;
      inserts.push(
        buildOccurrenceRow({
          id: crypto.randomUUID(),
          workspaceSlug,
          title: root.title,
          description: root.description,
          status: 'todo',
          progress: 0,
          todoDate,
          requestOffsetDays: requestOffset,
          deadlineOffsetDays: deadlineOffset,
          assigneeEmail: root.assignee_email,
          createdBy: root.created_by,
          tripId: root.trip_id,
          seriesId,
          rule,
          index: nextIndex,
        }),
      );
      nextIndex += 1;
    }
  }

  if (inserts.length === 0) return;
  const { error: insertErr } = await db().from('calendar_tasks').insert(inserts);
  if (insertErr) throw new Error(formatSupabaseError(insertErr));
}

/**
 * Attach / update recurrence on an existing task (used from Calendar edit).
 * Converts a one-off into a series, or refreshes the series rule + ending date.
 */
export async function applyCalendarRecurrence(
  taskId: string,
  ruleInput: CalendarRecurrenceRule | null | undefined,
): Promise<CalendarTask> {
  const rule = normalizeRecurrenceRule(ruleInput ?? null);

  const { data: currentRaw, error: currentErr } = await db()
    .from('calendar_tasks')
    .select(TASK_SELECT)
    .eq('id', taskId)
    .single();
  if (currentErr) throw new Error(formatSupabaseError(currentErr));
  const current = currentRaw as CalendarTask;

  if (!rule) {
    if (current.recurrence_series_id) {
      const { error } = await db()
        .from('calendar_tasks')
        .update({ recurrence_rule: null, updated_at: new Date().toISOString() })
        .eq('recurrence_series_id', current.recurrence_series_id);
      if (error) throw new Error(formatSupabaseError(error));
    } else if (current.recurrence_rule) {
      const { error } = await db()
        .from('calendar_tasks')
        .update({ recurrence_rule: null, updated_at: new Date().toISOString() })
        .eq('id', taskId);
      if (error) throw new Error(formatSupabaseError(error));
    }
    const { data: fresh, error: freshErr } = await db()
      .from('calendar_tasks')
      .select(TASK_SELECT)
      .eq('id', taskId)
      .single();
    if (freshErr) throw new Error(formatSupabaseError(freshErr));
    return fresh as CalendarTask;
  }

  const seriesId = current.recurrence_series_id || crypto.randomUUID();
  const requestOffset = daysBetween(current.todo_date, current.request_date);
  const deadlineOffset = daysBetween(current.todo_date, current.deadline);

  if (!current.recurrence_series_id) {
    const { error: markErr } = await db()
      .from('calendar_tasks')
      .update({
        recurrence_series_id: seriesId,
        recurrence_rule: rule,
        recurrence_index: 0,
        updated_at: new Date().toISOString(),
      })
      .eq('id', taskId);
    if (markErr) throw new Error(formatSupabaseError(markErr));
  } else {
    const { error: syncErr } = await db()
      .from('calendar_tasks')
      .update({
        recurrence_rule: rule,
        updated_at: new Date().toISOString(),
      })
      .eq('recurrence_series_id', seriesId);
    if (syncErr) throw new Error(formatSupabaseError(syncErr));
  }

  // Drop future open occurrences past the new ending date.
  if (rule.until) {
    const { data: siblings, error: sibErr } = await db()
      .from('calendar_tasks')
      .select(TASK_SELECT)
      .eq('recurrence_series_id', seriesId)
      .gt('todo_date', rule.until)
      .eq('status', 'todo');
    if (sibErr) throw new Error(formatSupabaseError(sibErr));
    const toDelete = ((siblings ?? []) as CalendarTask[]).filter((t) => t.id !== taskId);
    if (toDelete.length > 0) {
      const { error: delErr } = await db()
        .from('calendar_tasks')
        .delete()
        .in(
          'id',
          toDelete.map((t) => t.id),
        );
      if (delErr) throw new Error(formatSupabaseError(delErr));
    }
  }

  const { data: seriesRows, error: seriesErr } = await db()
    .from('calendar_tasks')
    .select(TASK_SELECT)
    .eq('recurrence_series_id', seriesId)
    .order('recurrence_index', { ascending: true });
  if (seriesErr) throw new Error(formatSupabaseError(seriesErr));
  const existing = (seriesRows ?? []) as CalendarTask[];
  const existingTodos = new Set(existing.map((t) => t.todo_date));
  const maxIndex = existing.reduce((m, t) => Math.max(m, t.recurrence_index), 0);
  const root = existing.find((t) => t.id === taskId) ?? existing[0] ?? current;

  const dates = listRecurrenceTodoDates(root.todo_date, rule, {
    maxCount: 80,
    horizonYmd: rule.until || horizonYmd(12),
  });

  const inserts: Record<string, unknown>[] = [];
  let nextIndex = maxIndex + 1;
  for (const todoDate of dates) {
    if (existingTodos.has(todoDate)) continue;
    if (todoDate <= root.todo_date) continue;
    inserts.push(
      buildOccurrenceRow({
        id: crypto.randomUUID(),
        workspaceSlug: root.workspace_slug,
        title: root.title,
        description: root.description,
        status: 'todo',
        progress: 0,
        todoDate,
        requestOffsetDays: requestOffset,
        deadlineOffsetDays: deadlineOffset,
        assigneeEmail: root.assignee_email,
        createdBy: root.created_by,
        tripId: root.trip_id,
        seriesId,
        rule,
        index: nextIndex,
      }),
    );
    nextIndex += 1;
  }

  if (inserts.length > 0) {
    const { error: insertErr } = await db().from('calendar_tasks').insert(inserts);
    if (insertErr) throw new Error(formatSupabaseError(insertErr));
  }

  const { data: fresh, error: freshErr } = await db()
    .from('calendar_tasks')
    .select(TASK_SELECT)
    .eq('id', taskId)
    .single();
  if (freshErr) throw new Error(formatSupabaseError(freshErr));
  return fresh as CalendarTask;
}

export type CalendarSeriesEditScope = 'this' | 'series';

/**
 * Copy shared content from one occurrence onto every task in the same recurrence series.
 * Keeps each occurrence's todo_date and status; request/deadline follow the same day offsets.
 */
export async function syncCalendarSeriesContent(sourceId: string): Promise<void> {
  const { data: sourceRaw, error: sourceErr } = await db()
    .from('calendar_tasks')
    .select(TASK_SELECT)
    .eq('id', sourceId)
    .single();
  if (sourceErr) throw new Error(formatSupabaseError(sourceErr));
  const source = sourceRaw as CalendarTask;
  if (!source.recurrence_series_id) return;

  const requestOffset = daysBetween(source.todo_date, source.request_date);
  const deadlineOffset = daysBetween(source.todo_date, source.deadline);

  const { data: siblingsRaw, error: sibErr } = await db()
    .from('calendar_tasks')
    .select(TASK_SELECT)
    .eq('recurrence_series_id', source.recurrence_series_id);
  if (sibErr) throw new Error(formatSupabaseError(sibErr));
  const siblings = (siblingsRaw ?? []) as CalendarTask[];

  for (const sib of siblings) {
    if (sib.id === source.id) continue;
    const todo = parseYmd(sib.todo_date);
    const { error } = await db()
      .from('calendar_tasks')
      .update({
        title: source.title,
        description: source.description,
        assignee_email: source.assignee_email,
        request_date: formatYmd(addCalendarDays(todo, requestOffset)),
        deadline: formatYmd(addCalendarDays(todo, deadlineOffset)),
        updated_at: new Date().toISOString(),
      })
      .eq('id', sib.id);
    if (error) throw new Error(formatSupabaseError(error));
  }
}

/** Save task fields, optionally push content to the whole series, then refresh recurrence. */
export async function saveCalendarTaskEdit(input: {
  id: string;
  patch: Partial<CalendarTaskInput> & { status?: CalendarTaskStatus; progress?: number };
  recurrenceRule?: CalendarRecurrenceRule | null;
  seriesScope?: CalendarSeriesEditScope;
}): Promise<CalendarTask> {
  await updateCalendarTask(input.id, input.patch);

  const { data: currentRaw, error: currentErr } = await db()
    .from('calendar_tasks')
    .select(TASK_SELECT)
    .eq('id', input.id)
    .single();
  if (currentErr) throw new Error(formatSupabaseError(currentErr));
  const current = currentRaw as CalendarTask;

  if (input.seriesScope === 'series' && current.recurrence_series_id) {
    await syncCalendarSeriesContent(input.id);
  }

  if (input.recurrenceRule !== undefined) {
    return applyCalendarRecurrence(input.id, input.recurrenceRule);
  }

  const { data: fresh, error: freshErr } = await db()
    .from('calendar_tasks')
    .select(TASK_SELECT)
    .eq('id', input.id)
    .single();
  if (freshErr) throw new Error(formatSupabaseError(freshErr));
  return fresh as CalendarTask;
}

export async function updateCalendarTask(
  id: string,
  patch: Partial<CalendarTaskInput> & { status?: CalendarTaskStatus; progress?: number },
): Promise<CalendarTask> {
  const updates: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (patch.title != null) updates.title = patch.title.trim();
  if (patch.description != null) updates.description = patch.description.trim();
  if (patch.status != null) updates.status = patch.status;
  if (patch.progress != null) updates.progress = patch.progress;
  if (patch.request_date != null) updates.request_date = patch.request_date;
  if (patch.todo_date != null) updates.todo_date = patch.todo_date;
  if (patch.deadline != null) updates.deadline = patch.deadline;
  if (patch.assignee_email != null) updates.assignee_email = normalizeEmail(patch.assignee_email);
  if (patch.trip_id !== undefined) updates.trip_id = patch.trip_id;
  if (patch.thread_id != null) updates.thread_id = patch.thread_id;
  if (patch.thread_order != null) updates.thread_order = patch.thread_order;
  if (patch.recurrence_rule !== undefined) {
    updates.recurrence_rule = normalizeRecurrenceRule(patch.recurrence_rule);
  }

  const { data, error } = await db()
    .from('calendar_tasks')
    .update(updates)
    .eq('id', id)
    .select(TASK_SELECT)
    .single();
  if (error) throw new Error(formatSupabaseError(error));
  const updated = data as CalendarTask;

  // Linked timeline: status is shared across the whole thread.
  if (patch.status != null) {
    const { error: syncErr } = await db()
      .from('calendar_tasks')
      .update({ status: patch.status, updated_at: new Date().toISOString() })
      .eq('thread_id', updated.thread_id);
    if (syncErr) throw new Error(formatSupabaseError(syncErr));
  }

  // Keep series rule copies in sync when editing recurrence on any occurrence.
  if (patch.recurrence_rule !== undefined && updated.recurrence_series_id) {
    const { error: seriesErr } = await db()
      .from('calendar_tasks')
      .update({
        recurrence_rule: updated.recurrence_rule,
        updated_at: new Date().toISOString(),
      })
      .eq('recurrence_series_id', updated.recurrence_series_id);
    if (seriesErr) throw new Error(formatSupabaseError(seriesErr));
  }

  // First node owns request/deadline for the whole linked timeline.
  const thread = await listThreadTasks(updated.thread_id);
  const first = threadRootTask(thread);
  const isFirst = first?.id === updated.id;

  if (isFirst && (patch.request_date != null || patch.deadline != null)) {
    const cascade: Record<string, unknown> = { updated_at: new Date().toISOString() };
    if (patch.request_date != null) cascade.request_date = patch.request_date;
    if (patch.deadline != null) cascade.deadline = patch.deadline;
    const { error: cascadeErr } = await db()
      .from('calendar_tasks')
      .update(cascade)
      .eq('thread_id', updated.thread_id)
      .neq('id', updated.id);
    if (cascadeErr) throw new Error(formatSupabaseError(cascadeErr));
  } else if (!isFirst && first) {
    const { error: alignErr } = await db()
      .from('calendar_tasks')
      .update({
        request_date: first.request_date,
        deadline: first.deadline,
        updated_at: new Date().toISOString(),
      })
      .eq('id', updated.id);
    if (alignErr) throw new Error(formatSupabaseError(alignErr));
  }

  const { data: fresh, error: freshErr } = await db()
    .from('calendar_tasks')
    .select(TASK_SELECT)
    .eq('id', id)
    .single();
  if (freshErr) throw new Error(formatSupabaseError(freshErr));
  return fresh as CalendarTask;
}

export async function deleteCalendarTask(id: string): Promise<void> {
  const { error } = await db().from('calendar_tasks').delete().eq('id', id);
  if (error) throw new Error(formatSupabaseError(error));
}

export async function listCalendarTrips(workspaceSlug: string): Promise<CalendarTrip[]> {
  const { data, error } = await db()
    .from('calendar_trips')
    .select(TRIP_SELECT)
    .eq('workspace_slug', workspaceSlug)
    .order('starts_on', { ascending: true });
  if (error) throw new Error(formatSupabaseError(error));
  return (data ?? []) as CalendarTrip[];
}

export async function listCalendarTripsInRange(
  workspaceSlug: string,
  from: string,
  to: string,
): Promise<CalendarTrip[]> {
  const { data, error } = await db()
    .from('calendar_trips')
    .select(TRIP_SELECT)
    .eq('workspace_slug', workspaceSlug)
    .lte('starts_on', to)
    .gte('ends_on', from)
    .order('starts_on', { ascending: true });
  if (error) throw new Error(formatSupabaseError(error));
  return (data ?? []) as CalendarTrip[];
}

export async function createCalendarTrip(
  workspaceSlug: string,
  input: CalendarTripInput,
  actorEmail: string,
): Promise<CalendarTrip> {
  const email = normalizeEmail(actorEmail);
  const row = {
    workspace_slug: workspaceSlug,
    title: input.title.trim(),
    location: (input.location ?? '').trim(),
    notes: (input.notes ?? '').trim(),
    starts_on: input.starts_on,
    ends_on: input.ends_on,
    owner_email: normalizeEmail(input.owner_email) || email,
    created_by: email,
    updated_at: new Date().toISOString(),
  };
  const { data, error } = await db().from('calendar_trips').insert(row).select(TRIP_SELECT).single();
  if (error) throw new Error(formatSupabaseError(error));
  return data as CalendarTrip;
}

export async function updateCalendarTrip(
  id: string,
  patch: Partial<CalendarTripInput>,
): Promise<CalendarTrip> {
  const updates: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (patch.title != null) updates.title = patch.title.trim();
  if (patch.location != null) updates.location = patch.location.trim();
  if (patch.notes != null) updates.notes = patch.notes.trim();
  if (patch.starts_on != null) updates.starts_on = patch.starts_on;
  if (patch.ends_on != null) updates.ends_on = patch.ends_on;
  if (patch.owner_email != null) updates.owner_email = normalizeEmail(patch.owner_email);

  const { data, error } = await db()
    .from('calendar_trips')
    .update(updates)
    .eq('id', id)
    .select(TRIP_SELECT)
    .single();
  if (error) throw new Error(formatSupabaseError(error));
  return data as CalendarTrip;
}

export async function deleteCalendarTrip(id: string): Promise<void> {
  const { error } = await db().from('calendar_trips').delete().eq('id', id);
  if (error) throw new Error(formatSupabaseError(error));
}

export function calendarStatusLabel(status: CalendarTaskStatus): string {
  if (status === 'in_progress') return 'In progress';
  if (status === 'done') return 'Done';
  return 'To do';
}

export async function listCalendarDirectory(): Promise<Map<string, string>> {
  const map = new Map<string, string>();

  const addRow = (emailRaw: unknown, displayRaw: unknown, usernameRaw: unknown) => {
    const email = normalizeEmail(String(emailRaw ?? ''));
    if (!email) return;
    const display = String(displayRaw ?? '').trim();
    const username = String(usernameRaw ?? '').trim();
    const label = display || username;
    if (!label) return;
    if (!map.has(email)) map.set(email, label);
  };

  try {
    const calendarRows = await fetchPortalMembersByKind('calendar');
    for (const row of calendarRows) {
      addRow(row.email, row.display_name, row.username);
    }
  } catch (err) {
    throw new Error(formatSupabaseError(err));
  }

  try {
    const crmRows = await fetchPortalMembersByKind('crm');
    for (const row of crmRows) {
      addRow(row.email, row.display_name, row.username);
    }
  } catch {
    /* optional enrichment */
  }

  return map;
}

export function calendarPersonLabel(
  email: string | null | undefined,
  directory?: Map<string, string>,
): string {
  const e = normalizeEmail(email);
  if (!e) return '';
  const fromDir = directory?.get(e)?.trim();
  if (fromDir) return fromDir;
  const local = e.split('@')[0] ?? e;
  const pretty = local.replace(/[._]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!pretty) return e;
  return pretty
    .split(' ')
    .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase() : part))
    .join(' ');
}

export async function listCalendarTaskAttachments(
  taskId: string,
): Promise<CalendarTaskAttachment[]> {
  const { data, error } = await db()
    .from('calendar_task_attachments')
    .select(ATTACH_SELECT)
    .eq('task_id', taskId)
    .order('created_at', { ascending: false });
  if (error) throw new Error(formatSupabaseError(error));
  return (data ?? []) as CalendarTaskAttachment[];
}

export async function listCalendarThreadAttachments(
  threadId: string,
): Promise<CalendarTaskAttachment[]> {
  const { data, error } = await db()
    .from('calendar_task_attachments')
    .select(ATTACH_SELECT)
    .eq('thread_id', threadId)
    .order('created_at', { ascending: false });
  if (error) throw new Error(formatSupabaseError(error));
  return (data ?? []) as CalendarTaskAttachment[];
}

export async function uploadCalendarTaskAttachment(input: {
  task: CalendarTask;
  file: File;
  actorEmail: string;
}): Promise<CalendarTaskAttachment> {
  const uploadedBy = normalizeEmail(input.actorEmail);
  if (!uploadedBy) throw new Error('Sign in required to upload.');
  const safeName = input.file.name.replace(/[^\w.\-()+ ]+/g, '_').trim() || 'file';
  const path = `${input.task.workspace_slug}/${input.task.thread_id}/${input.task.id}/${crypto.randomUUID()}-${safeName}`;

  const { error: upErr } = await db()
    .storage.from(CALENDAR_ATTACHMENTS_BUCKET)
    .upload(path, input.file, {
      cacheControl: '3600',
      upsert: false,
      contentType: input.file.type || 'application/octet-stream',
    });
  if (upErr) throw new Error(formatSupabaseError(upErr));

  const row = {
    task_id: input.task.id,
    thread_id: input.task.thread_id,
    file_name: input.file.name,
    content_type: input.file.type || 'application/octet-stream',
    byte_size: input.file.size,
    storage_path: path,
    uploaded_by: uploadedBy,
  };
  const { data, error } = await db()
    .from('calendar_task_attachments')
    .insert(row)
    .select(ATTACH_SELECT)
    .single();
  if (error) {
    await db().storage.from(CALENDAR_ATTACHMENTS_BUCKET).remove([path]).catch(() => undefined);
    throw new Error(formatSupabaseError(error));
  }
  return data as CalendarTaskAttachment;
}

export async function getCalendarAttachmentSignedUrl(storagePath: string): Promise<string> {
  const { data, error } = await db()
    .storage.from(CALENDAR_ATTACHMENTS_BUCKET)
    .createSignedUrl(storagePath, 60 * 30);
  if (error) throw new Error(formatSupabaseError(error));
  if (!data?.signedUrl) throw new Error('Could not create download link.');
  return data.signedUrl;
}

export async function deleteCalendarTaskAttachment(
  attachment: CalendarTaskAttachment,
): Promise<void> {
  const { error } = await db().from('calendar_task_attachments').delete().eq('id', attachment.id);
  if (error) throw new Error(formatSupabaseError(error));
  await db()
    .storage.from(CALENDAR_ATTACHMENTS_BUCKET)
    .remove([attachment.storage_path])
    .catch(() => undefined);
}

export function inviteCalendarEmail(email: string, displayName?: string): Promise<void> {
  return (async () => {
    const e = normalizeEmail(email);
    if (!e) throw new Error('Email is required.');

    const wide = await adminSetPortalRole({
      email: e,
      portal: 'calendar',
      role: 'user',
      displayName: displayName?.trim() || null,
    });
    if (wide === 'ok') return;
    throw new Error(
      'Calendar invite failed. Run migration 258 (admin_set_portal_role calendar invite) in Supabase.',
    );
  })();
}
